import React, { useState, useEffect } from "react";
import {
  Plus, RefreshCcw, Pencil, Trash2, MoreVertical, Scale,
  Droplets, Hash, Ruler, Box, Square, Settings, X,
  Building2, ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import uomService from "@/services/uomService";
import branchService from "@/services/branchService";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog, AlertDialog } from "@/components/Admin/Menu_Management/UIComponents";
import { P } from "@/config/permissionCodes";

const CATEGORY_STYLES = {
  WEIGHT: { icon: Scale,    color: 'bg-blue-100 text-blue-700' },
  VOLUME: { icon: Droplets, color: 'bg-cyan-100 text-cyan-700' },
  COUNT:  { icon: Hash,     color: 'bg-amber-100 text-amber-700' },
  LENGTH: { icon: Ruler,    color: 'bg-purple-100 text-purple-700' },
  AREA:   { icon: Square,   color: 'bg-green-100 text-green-700' },
  PACK:   { icon: Box,      color: 'bg-orange-100 text-orange-700' },
};

// ── Access Denied ────────────────────────────────────────────────────────────
function AccessDenied() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view Units of Measurement.</p>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function UomManagement() {
  const { user, hasPermission } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';

  // ── Permissions ────────────────────────────────────────────────────────────
  const canViewScreen  = hasPermission(P.INVENTORY.UOM.SCREEN);
  const canAdd         = hasPermission(P.INVENTORY.UOM.ADD);
  const canEdit        = hasPermission(P.INVENTORY.UOM.EDIT);
  const canDelete      = hasPermission(P.INVENTORY.UOM.DELETE);
  const canAddCat      = hasPermission(P.INVENTORY.UOM.CATEGORIES.ADD);
  const canEditCat     = hasPermission(P.INVENTORY.UOM.CATEGORIES.EDIT);
  const canDeleteCat   = hasPermission(P.INVENTORY.UOM.CATEGORIES.DELETE);
  const canManageCats  = canAddCat || canEditCat || canDeleteCat;

  if (!canViewScreen) return <AccessDenied />;

  // ── State ──────────────────────────────────────────────────────────────────
  const [branches, setBranches]             = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [showModal, setShowModal]           = useState(false);
  const [editData, setEditData]             = useState(null);
  const [uoms, setUoms]                     = useState([]);
  const [categories, setCategories]         = useState([]);
  const [loading, setLoading]               = useState(false);

  const [name, setName]                         = useState("");
  const [symbol, setSymbol]                     = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [conversionFactor, setConversionFactor] = useState("1");
  const [isBaseUnit, setIsBaseUnit]             = useState(false);
  const [isActive, setIsActive]                 = useState(true);

  const [openMenu, setOpenMenu]         = useState(null);
  const [dropDirection, setDropDirection] = useState("down");

  // Category modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editCategoryData, setEditCategoryData]   = useState(null);
  const [catCode, setCatCode]                     = useState("");
  const [catName, setCatName]                     = useState("");
  const [catDescription, setCatDescription]       = useState("");
  const [catBaseUomName, setCatBaseUomName]       = useState("");
  const [catBaseUomSymbol, setCatBaseUomSymbol]   = useState("");
  const [catIsActive, setCatIsActive]             = useState(true);
  const [catDisplayOrder, setCatDisplayOrder]     = useState("0");

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: "", message: "", onConfirm: null });
  const [alertDialog, setAlertDialog]     = useState({ isOpen: false, title: "", message: "", type: "error" });

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  const showAlert = (title, message, type = "error") =>
    setAlertDialog({ isOpen: true, title, message, type });

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const closeMenu = () => setOpenMenu(null);
    window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, []);

  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches()
      .then(result => { if (result?.length > 1) setBranches(result); })
      .catch(console.error);
  }, [isOwner]);

  useEffect(() => {
    loadCategories(selectedBranchId);
    loadUoms(selectedBranchId);
  }, [selectedBranchId]);

  // ── Data loaders ───────────────────────────────────────────────────────────
  const loadCategories = async (branchId) => {
    try {
      const res = await uomService.getAllCategories(branchId);
      setCategories(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error(err);
      showAlert("Error", "Failed to load UOM categories");
    }
  };

  const loadUoms = async (branchId) => {
    try {
      setLoading(true);
      const res = await uomService.getAll({}, branchId);
      setUoms(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error(err);
      showAlert("Error", "Failed to load UOMs");
    } finally {
      setLoading(false);
    }
  };

  // ── UOM modal ──────────────────────────────────────────────────────────────
  const openModal = (data = null) => {
    setEditData(data);
    setName(data?.name || "");
    setSymbol(data?.symbol || "");
    setSelectedCategoryId(data?.uomCategory?.id?.toString() || "");
    setConversionFactor(data?.conversionFactor?.toString() || "1");
    setIsBaseUnit(data?.isBaseUnit ?? false);
    setIsActive(data?.isActive ?? true);
    setShowModal(true);
  };

  const getSelectedCategory = () =>
    categories.find(c => c.id?.toString() === selectedCategoryId);

  const handleSave = async () => {
    if (!name.trim() || !symbol.trim()) {
      showAlert("Validation Error", "Name & Symbol are required."); return;
    }
    if (!selectedCategoryId) {
      showAlert("Validation Error", "Please select a category."); return;
    }
    const factor = parseFloat(conversionFactor) || 1;
    if (factor <= 0) {
      showAlert("Validation Error", "Conversion factor must be greater than 0."); return;
    }

    const payload = {
      name, symbol,
      uomCategory: { id: parseInt(selectedCategoryId) },
      conversionFactor: factor,
      isBaseUnit,
    };

    try {
      if (editData) {
        await uomService.update(editData.id, { ...payload, id: editData.id, isActive, isDeleted: editData.isDeleted ?? false });
        showToast("UOM updated successfully");
      } else {
        await uomService.create({ ...payload, isActive: true, isDeleted: false });
        showToast("UOM added successfully");
      }
      setShowModal(false);
      loadUoms(selectedBranchId);
    } catch (err) {
      console.error(err);
      showAlert("Error", "Failed to save UOM");
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Unit of Measurement",
      message: "Are you sure you want to delete this UOM?",
      onConfirm: () => confirmDelete(id),
    });
  };

  const confirmDelete = async (id) => {
    try {
      await uomService.delete(id);
      showToast("UOM deleted");
      loadUoms(selectedBranchId);
    } catch (err) {
      console.error(err);
      showAlert("Error", "Failed to delete UOM");
    }
  };

  // ── Category modal ─────────────────────────────────────────────────────────
  const openCategoryModal = (data = null) => {
    setEditCategoryData(data);
    setCatCode(data?.code || "");
    setCatName(data?.name || "");
    setCatDescription(data?.description || "");
    setCatBaseUomName(data?.baseUomName || "");
    setCatBaseUomSymbol(data?.baseUomSymbol || "");
    setCatIsActive(data?.isActive ?? true);
    setCatDisplayOrder(data?.displayOrder?.toString() || "0");
    setShowCategoryModal(true);
  };

  const resetCategoryForm = () => {
    setEditCategoryData(null);
    setCatCode(""); setCatName(""); setCatDescription("");
    setCatBaseUomName(""); setCatBaseUomSymbol("");
    setCatIsActive(true); setCatDisplayOrder("0");
  };

  const handleSaveCategory = async () => {
    if (!catCode.trim() || !catName.trim()) {
      showAlert("Validation Error", "Code and Name are required."); return;
    }
    const payload = {
      code: catCode.toUpperCase(), name: catName, description: catDescription,
      baseUomName: catBaseUomName, baseUomSymbol: catBaseUomSymbol,
      isActive: catIsActive, displayOrder: parseInt(catDisplayOrder) || 0,
    };
    try {
      if (editCategoryData) {
        await uomService.updateCategory(editCategoryData.id, payload);
        showToast("Category updated successfully");
      } else {
        await uomService.createCategory(payload);
        showToast("Category added successfully");
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      loadCategories(selectedBranchId);
    } catch (err) {
      console.error(err);
      showAlert("Error", err.response?.data?.message || "Failed to save category");
    }
  };

  const handleDeleteCategory = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Category",
      message: "Are you sure you want to delete this category? This will only work if no UoMs are using it.",
      onConfirm: () => confirmDeleteCategory(id),
    });
  };

  const confirmDeleteCategory = async (id) => {
    try {
      await uomService.deleteCategory(id);
      showToast("Category deleted");
      loadCategories(selectedBranchId);
    } catch (err) {
      console.error(err);
      showAlert("Error", err.response?.data?.message || "Failed to delete category");
    }
  };

  const hasAnyRowAction = canEdit || canDelete;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 flex flex-col min-h-screen">

      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl font-bold text-slate-900">Units of Measurement</h1>

        <div className="flex gap-3">
          {branches.length > 1 && (
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={selectedBranchId ?? ''}
                onChange={(e) => setSelectedBranchId(e.target.value || null)}
                className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
              >
                <option value="">All Branches</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          )}

          <button
            onClick={() => { loadUoms(selectedBranchId); loadCategories(selectedBranchId); }}
            className="h-10 w-10 flex items-center justify-center border border-slate-300 rounded-lg hover:bg-slate-100"
            title="Refresh"
          >
            <RefreshCcw size={18} className="text-slate-700" />
          </button>

          {/* Categories button — only if user can manage at least one category action */}
          {canManageCats && (
            <button
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg flex items-center gap-2 hover:bg-slate-100"
              onClick={() => openCategoryModal()}
              title="Manage Categories"
            >
              <Settings size={18} />
              Categories
            </button>
          )}

          {/* Add UOM button */}
          {canAdd && (
            <button
              className="px-4 py-2 bg-teal-600 text-white rounded-lg flex items-center gap-2"
              onClick={() => openModal()}
            >
              <Plus size={18} />
              Add UOM
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-5 text-center text-slate-500">Loading...</div>
        ) : (
          <table className="w-full text-left">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Symbol</th>
                <th className="p-3">Category</th>
                <th className="p-3">Conversion Factor</th>
                <th className="p-3">Status</th>
                {hasAnyRowAction && <th className="p-3">Actions</th>}
              </tr>
            </thead>

            <tbody>
              {uoms.length === 0 && (
                <tr>
                  <td colSpan={hasAnyRowAction ? 6 : 5} className="p-5 text-center text-slate-500">
                    No UOM Found
                  </td>
                </tr>
              )}

              {uoms.map((uom) => {
                const categoryCode  = uom.uomCategory?.code;
                const categoryStyle = CATEGORY_STYLES[categoryCode] || {};
                const CategoryIcon  = categoryStyle.icon || Hash;

                return (
                  <tr key={uom.id} className="border-t hover:bg-slate-50">
                    {/* Name */}
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{uom.name}</span>
                        {uom.isBaseUnit && (
                          <span className="px-1.5 py-0.5 bg-teal-100 text-teal-700 text-[10px] font-medium rounded">BASE</span>
                        )}
                      </div>
                    </td>

                    {/* Symbol */}
                    <td className="p-3">
                      <span className="px-2 py-1 bg-slate-100 rounded font-mono text-sm">{uom.symbol}</span>
                    </td>

                    {/* Category */}
                    <td className="p-3">
                      {uom.uomCategory ? (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${categoryStyle.color || 'bg-slate-100 text-slate-700'}`}>
                          <CategoryIcon size={12} />
                          {uom.uomCategory.name}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>

                    {/* Conversion Factor */}
                    <td className="p-3">
                      <span className="text-sm">
                        {uom.conversionFactor || 1}
                        {uom.uomCategory && (
                          <span className="text-slate-400 text-xs ml-1">
                            → 1 {uom.uomCategory.baseUomName} ({uom.uomCategory.baseUomSymbol})
                          </span>
                        )}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="p-3">
                      {uom.isActive
                        ? <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-lg">Active</span>
                        : <span className="px-3 py-1 bg-red-100 text-red-600 text-sm rounded-lg">Inactive</span>
                      }
                    </td>

                    {/* Actions */}
                    {hasAnyRowAction && (
                      <td className="p-3">
                        <div className="relative inline-block">
                          <button
                            className="h-8 w-8 flex items-center justify-center hover:bg-slate-100 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect   = e.currentTarget.getBoundingClientRect();
                              setDropDirection(window.innerHeight - rect.bottom < 150 ? "up" : "down");
                              setOpenMenu(openMenu === uom.id ? null : uom.id);
                            }}
                          >
                            <MoreVertical size={18} />
                          </button>

                          {openMenu === uom.id && (
                            <div
                              className={`absolute w-32 bg-white rounded-lg shadow-lg border z-20
                                ${dropDirection === "down" ? "top-0 left-full ml-2" : "bottom-0 left-full ml-2"}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canEdit && (
                                <button
                                  className="w-full px-4 py-2 hover:bg-slate-100 text-sm flex items-center gap-2 text-gray-700"
                                  onClick={() => { openModal(uom); setOpenMenu(null); }}
                                >
                                  <Pencil size={14} className="text-gray-600" /> Edit
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  className="w-full px-4 py-2 hover:bg-slate-100 text-sm flex items-center gap-2 text-red-600"
                                  onClick={() => { handleDelete(uom.id); setOpenMenu(null); }}
                                >
                                  <Trash2 size={14} className="text-red-600" /> Delete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* UOM Add/Edit Modal */}
      {showModal && (canAdd || canEdit) && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl">
            <h2 className="text-xl font-bold mb-4">{editData ? "Edit UOM" : "Add UOM"}</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600 font-medium">Name *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full border rounded-lg p-2 mt-1" placeholder="e.g., Kilogram" />
              </div>
              <div>
                <label className="text-sm text-slate-600 font-medium">Symbol *</label>
                <input type="text" value={symbol} onChange={(e) => setSymbol(e.target.value)}
                  className="w-full border rounded-lg p-2 mt-1" placeholder="e.g., kg" />
              </div>
              <div>
                <label className="text-sm text-slate-600 font-medium">Category *</label>
                <select value={selectedCategoryId} onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="w-full border rounded-lg p-2 mt-1">
                  <option value="">Select Category</option>
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600 font-medium">Conversion Factor</label>
                <input type="number" step="any" min="0.000001" value={conversionFactor}
                  onChange={(e) => setConversionFactor(e.target.value)}
                  className="w-full border rounded-lg p-2 mt-1" placeholder="e.g., 1000" />
                {selectedCategoryId && (
                  <p className="text-xs text-slate-400 mt-1">
                    1 {name || 'unit'} = {conversionFactor} {getSelectedCategory()?.baseUomName || 'base units'} ({getSelectedCategory()?.baseUomSymbol || ''})
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={isBaseUnit}
                    onChange={(e) => { setIsBaseUnit(e.target.checked); if (e.target.checked) setConversionFactor("1"); }}
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                  <span className="text-sm text-slate-700">
                    This is the base unit for {getSelectedCategory()?.name || 'this category'}
                  </span>
                </label>
                <p className="text-xs text-slate-400 ml-6 mt-1">
                  Base units have conversion factor of 1 (e.g., Gram for Weight, Milliliter for Volume)
                </p>
              </div>
              {editData && (
                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300 transition-colors"
                onClick={() => setShowModal(false)}>Cancel</button>
              <button className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                onClick={handleSave}>{editData ? "Update" : "Create"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && canManageCats && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">

            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-bold">
                {editCategoryData ? "Edit Category" : "Manage UoM Categories"}
              </h2>
              <button onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}
                className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">

              {/* Add/Edit Form — shown only if user can add or edit */}
              {(canAddCat || (canEditCat && editCategoryData)) && (
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {editCategoryData ? "Edit Category" : "Add New Category"}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-slate-600 font-medium">Code *</label>
                      <input type="text" value={catCode}
                        onChange={(e) => setCatCode(e.target.value.toUpperCase())}
                        className="w-full border rounded-lg p-2 mt-1 text-sm" placeholder="e.g., WEIGHT"
                        disabled={!!editCategoryData} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium">Name *</label>
                      <input type="text" value={catName} onChange={(e) => setCatName(e.target.value)}
                        className="w-full border rounded-lg p-2 mt-1 text-sm" placeholder="e.g., Weight" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium">Description</label>
                      <input type="text" value={catDescription} onChange={(e) => setCatDescription(e.target.value)}
                        className="w-full border rounded-lg p-2 mt-1 text-sm" placeholder="e.g., Weight measurements" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium">Base UoM Name</label>
                      <input type="text" value={catBaseUomName} onChange={(e) => setCatBaseUomName(e.target.value)}
                        className="w-full border rounded-lg p-2 mt-1 text-sm" placeholder="e.g., Gram" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium">Base UoM Symbol</label>
                      <input type="text" value={catBaseUomSymbol} onChange={(e) => setCatBaseUomSymbol(e.target.value)}
                        className="w-full border rounded-lg p-2 mt-1 text-sm" placeholder="e.g., g" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 font-medium">Display Order</label>
                      <input type="number" value={catDisplayOrder} onChange={(e) => setCatDisplayOrder(e.target.value)}
                        className="w-full border rounded-lg p-2 mt-1 text-sm" placeholder="0" />
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={catIsActive} onChange={(e) => setCatIsActive(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-teal-600" />
                      <span className="text-sm text-slate-700">Active</span>
                    </label>
                    <div className="flex-1" />
                    {editCategoryData && (
                      <button onClick={resetCategoryForm}
                        className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded">
                        Cancel Edit
                      </button>
                    )}
                    <button onClick={handleSaveCategory}
                      className="px-4 py-1.5 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">
                      {editCategoryData ? "Update" : "Add Category"}
                    </button>
                  </div>
                </div>
              )}

              {/* Categories Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Code</th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Base Unit</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Order</th>
                      {(canEditCat || canDeleteCat) && <th className="p-2 text-left">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 && (
                      <tr>
                        <td colSpan={(canEditCat || canDeleteCat) ? 6 : 5}
                          className="p-4 text-center text-slate-500">No categories found</td>
                      </tr>
                    )}
                    {categories.map((cat) => {
                      const style       = CATEGORY_STYLES[cat.code] || {};
                      const CategoryIcon = style.icon || Hash;
                      return (
                        <tr key={cat.id} className="border-t hover:bg-slate-50">
                          <td className="p-2">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${style.color || 'bg-slate-100 text-slate-700'}`}>
                              <CategoryIcon size={12} /> {cat.code}
                            </span>
                          </td>
                          <td className="p-2 font-medium">{cat.name}</td>
                          <td className="p-2">
                            {cat.baseUomName && (
                              <span className="text-slate-600">{cat.baseUomName} ({cat.baseUomSymbol})</span>
                            )}
                          </td>
                          <td className="p-2">
                            {cat.isActive
                              ? <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Active</span>
                              : <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">Inactive</span>
                            }
                          </td>
                          <td className="p-2 text-slate-500">{cat.displayOrder}</td>
                          {(canEditCat || canDeleteCat) && (
                            <td className="p-2">
                              <div className="flex gap-1">
                                {canEditCat && (
                                  <button onClick={() => openCategoryModal(cat)}
                                    className="p-1.5 hover:bg-slate-100 rounded text-slate-600" title="Edit">
                                    <Pencil size={14} />
                                  </button>
                                )}
                                {canDeleteCat && (
                                  <button onClick={() => handleDeleteCategory(cat.id)}
                                    className="p-1.5 hover:bg-red-50 rounded text-red-500" title="Delete">
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end p-4 border-t">
              <button onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}
                className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        type="danger"
        onConfirm={() => { confirmDialog.onConfirm(); setConfirmDialog({ isOpen: false }); }}
        onClose={() => setConfirmDialog({ isOpen: false })}
      />
      <AlertDialog
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ isOpen: false, title: "", message: "", type: "error" })}
      />
    </div>
  );
}